import { useEffect, useRef } from 'react';
import Order from 'communication/types/order/Order';
import { toTradeOptimizerOrder } from 'widgets/tradeOptimizer/mapping';
import {
    AddBasketPayload,
    AuthenticationStatusChangedMessagePayload,
    ReceiveMessageErrorPayload,
    TradeOptimizerOutTopic,
} from 'widgets/tradeOptimizer/types';
import { v4 as uuid } from 'uuid';

export interface TradeOptimizerPopupManagerOptions {
    onSendBasketError: (err: ReceiveMessageErrorPayload) => void;
    onAuthenticationStatusChanged: (
        msg: AuthenticationStatusChangedMessagePayload
    ) => void;
}

export class TradeOptimizerPopupManager {
    private popup: Window | null = null;
    private _dispose: () => void;
    private onSendBasketError: (err: ReceiveMessageErrorPayload) => void;
    private onAuthenticationStatusChanged: (
        msg: AuthenticationStatusChangedMessagePayload
    ) => void;

    public constructor(options: TradeOptimizerPopupManagerOptions) {
        const handler = this.handleEvent.bind(this);
        window.addEventListener('message', handler);
        this._dispose = () => window.removeEventListener('message', handler);
        this.onSendBasketError = options.onSendBasketError;
        this.onAuthenticationStatusChanged =
            options.onAuthenticationStatusChanged;
    }

    public get isOpen() {
        return !!this.popup && !this.popup.closed;
    }

    public dispose() {
        this._dispose();
    }

    public openPopup(url: string) {
        this.popup = window.open(url, 'TradeOptimizerPopup', 'popup=1');
    }

    public sendBasket(orders: Order[]) {
        this.sendMessage<AddBasketPayload>('ADD_BASKET', {
            orders: orders.map(toTradeOptimizerOrder),
            correlationId: uuid(),
        });
    }

    public sendAuthenticate(token: string) {
        this.sendMessage('AUTHENTICATE', {
            token,
            application: 'Indigo',
            applicationVersion: '1.0.0',
            correlationId: uuid(),
        });
    }

    public sendMessage<T extends { correlationId: string }>(
        topic: string,
        payload: T
    ) {
        if (!this.popup) {
            throw new Error('popup is not open');
        }
        this.popup.postMessage(
            JSON.stringify({
                topic,
                payload,
            }),
            { targetOrigin: '*' }
        );
    }

    private handleEvent(
        e: MessageEvent<string | { source: string } | { type: string }>
    ) {
        try {
            if (typeof e.data !== 'string') {
                throw new Error('Data is not a string');
            }

            const received = JSON.parse(e.data);

            if (!received.type) {
                throw new Error('Expected "type" property not found');
            }

            this.handleMessage(received.type, received.payload);
        } catch (err) {
            // Ignore message...
        }
    }

    private handleMessage(type: TradeOptimizerOutTopic, payload?: unknown) {
        if (type === 'RECEIVE_MESSAGE_ERROR') {
            this.onSendBasketError(payload as ReceiveMessageErrorPayload);
        }
        if (type === 'AUTHENTICATION_STATUS_CHANGED') {
            this.onAuthenticationStatusChanged(
                payload as AuthenticationStatusChangedMessagePayload
            );
        }
    }
}

export function useTradeOptimizerPopup(
    options: TradeOptimizerPopupManagerOptions
) {
    const manager = useRef<TradeOptimizerPopupManager>();
    useEffect(() => {
        manager.current = new TradeOptimizerPopupManager(options);
        return () => {
            manager.current!.dispose();
        };
    }, [options]);
    return manager.current;
}
